// react
import { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// components
import { UniversalLinkHandler } from 'router';
import { Toast } from '@karehero/llama';

// hooks
import { useAuth } from 'hooks';

// store
import { useSelector, dispatch } from 'store';
import {
  getToastQueue,
  newToast,
  removeToast,
  ToastQueueItem,
} from 'store/slices/toastSlice';
import { useSignOutMutation } from 'store/api/iam';
import { getAccount } from 'store/slices/iamSlice';
import { usePushNotification } from 'hooks/usePushNotification';
import useIntercom from 'hooks/useIntercom';

/**
 * RootLayout provides the UniversalLinkHandler component with an Outlet.
 */
export const RootLayout = () => {
  // state
  const [currentToast, setCurrentToast] = useState<ToastQueueItem | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);

  // hooks
  const { isAuthenticated, isLoading } = useAuth();
  const [signOutAPI] = useSignOutMutation();
  const toastQueue = useSelector(getToastQueue);
  const account = useSelector(getAccount);
  const location = useLocation();
  const { registerForPushNotifications, cleanup } = usePushNotification();
  const { mountIntercom, shutdown } = useIntercom();

  // effects
  useEffect(() => {
    setIsToastOpen(toastQueue.length > 0);
    setTimeout(() => {
      setCurrentToast(toastQueue[0]);
      setIsToastOpen(true);
    }, 200);
  }, [toastQueue]);

  useEffect(() => {
    if (!isToastOpen) {
      dispatch(removeToast());
    }
  }, [isToastOpen]);

  useEffect(() => {
    if (isAuthenticated || isLoading || !account) return;

    signOutAPI().catch((err) => {
      console.error(`Error signing out: `, err);
      dispatch(
        newToast({
          title: 'Error signing out',
          description: 'Please try again',
          variant: 'error',
        }),
      );
    });
  }, [account, isAuthenticated, isLoading, signOutAPI]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return () => {};
    }
    registerForPushNotifications().catch((err) => {
      console.error('Error registering for push notifications:', err);
    });

    return cleanup;
  }, [isLoading, isAuthenticated, registerForPushNotifications, cleanup]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isLoading &&
      account &&
      account.id &&
      account.accountRole?.name !== 'KareHero Administrator' &&
      account.accountRole?.name !== 'Organization Owner'
    ) {
      mountIntercom();
    }
    return () => shutdown();
  }, [isAuthenticated, isLoading, account, mountIntercom, shutdown]);

  const isOverrideBottom = useMemo(
    () => location.pathname === '/register' || location.pathname === '/sign-in',
    [location.pathname],
  );

  return (
    <>
      <UniversalLinkHandler />
      {currentToast && (
        <Toast
          title={currentToast?.title || ''}
          description={currentToast?.description || ''}
          variant={currentToast?.variant || 'plain'}
          isOpen={isToastOpen}
          setIsOpen={setIsToastOpen}
          size='sm'
          isOverrideBottom={isOverrideBottom}
        />
      )}
      <Outlet />
    </>
  );
};
